import React from "react";

import styled from "styled-components";
import { Checkmark, Close, Search } from "@carbon/icons-react";
import { mergeOverrides } from "baseui";
import { Overrides } from "baseui/overrides";
import { Input as BaseInput, InputProps as BaseInputProps, SIZE } from "baseui/input";

import baseuiTheme from "../../../../styling/baseui_theme";
import InputClearIcon from "../shared/input_clear_icon";
import { StyledCharacterCounter } from "../shared/shared_styles";

export * from "baseui/input";

export enum STATUS {
  Default = "default",
  Complete = "complete",
  Incomplete = "incomplete",
}

export interface InputProps extends Omit<BaseInputProps, "endEnhancer" | "startEnhancer"> {
  emphasis?: boolean;
  status?: STATUS;
  search?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  dataTest?: string;
  "data-analytics-id"?: string;
  currentCharacterCount?: number;
  maxCharacterCount?: number;
}

const CheckmarkIcon = styled(Checkmark)`
  fill: ${baseuiTheme.colors.contentPositive};
`;

const CloseIcon = styled(Close)`
  fill: ${baseuiTheme.colors.contentNegative};
`;

export const getIconSize = ($size: InputProps["size"]) => {
  let size = baseuiTheme.typography.font350.lineHeight;
  switch ($size) {
    case SIZE.mini:
      size = baseuiTheme.typography.font150.lineHeight;
      break;
    case SIZE.compact:
      size = baseuiTheme.typography.font250.lineHeight;
      break;
    case SIZE.large:
      size = baseuiTheme.typography.font300.lineHeight;
      break;
  }

  return parseInt(size as string);
};

export const getOverrides = (
  props: InputProps,
): Overrides<
  BaseInput & { $isFocused: boolean; $disabled: boolean; $error: boolean; $positive: boolean }
> => {
  const iconSize = getIconSize(props.size);
  return {
    Root: {
      style: ({ $theme, $isFocused, $disabled, $positive, $error }) => ({
        ...(!!getEndEnhancer(props) && { paddingRight: "0" }),
        ...(!$positive &&
          !$error && {
            backgroundColor: $theme.colors.backgroundAlwaysLight,
            ...(!$isFocused && {
              borderTopColor: $theme.colors.mono600,
              borderBottomColor: $theme.colors.mono600,
              borderLeftColor: $theme.colors.mono600,
              borderRightColor: $theme.colors.mono600,
            }),
            ...($isFocused && {
              backgroundColor: $theme.colors.mono100,
            }),
            ...($disabled && {
              borderTopColor: $theme.colors.mono400,
              borderBottomColor: $theme.colors.mono400,
              borderLeftColor: $theme.colors.mono400,
              borderRightColor: $theme.colors.mono400,
            }),
          }),
        borderTopLeftRadius: "0px",
        borderTopRightRadius: "0px",
        borderBottomLeftRadius: "0px",
        borderBottomRightRadius: "0px",
      }),
    },
    StartEnhancer: {
      style: ({ $theme, $positive, $error, $isFocused }) => {
        if (!$positive && !$error) {
          return {
            backgroundColor: $isFocused
              ? $theme.colors.mono100
              : $theme.colors.backgroundAlwaysLight,
          };
        }
      },
    },
    EndEnhancer: {
      style: ({ $theme, $positive, $error }) => {
        if (!$positive && !$error) {
          return {
            backgroundColor: $theme.colors.backgroundAlwaysLight,
          };
        }
      },
    },
    Input: {
      style: ({ $theme, $positive, $error, $isFocused }) => {
        return {
          "::placeholder": {
            color: $theme.colors.mono600,
          },
          ...(props.emphasis ? { fontSize: $theme.typography.font650.fontSize } : {}),

          ...(!$positive &&
            !$error && {
              backgroundColor: $isFocused
                ? $theme.colors.mono100
                : $theme.colors.backgroundAlwaysLight,
            }),
          paddingRight: "0",
        };
      },
      props: {
        "data-test": props.dataTest,
        "data-analytics-id": props["data-analytics-id"],
      },
    },
    InputContainer: {
      style: ({ $theme, $positive, $error, $isFocused }) => {
        return {
          ...(!$positive &&
            !$error && {
              backgroundColor: $isFocused
                ? $theme.colors.mono100
                : $theme.colors.backgroundAlwaysLight,
            }),
          paddingRight: "0",
        };
      },
    },
    ClearIconContainer: {
      style: ({ $theme, $positive, $error, $isFocused }) => {
        if (!$positive && !$error) {
          return {
            backgroundColor: $isFocused
              ? $theme.colors.mono100
              : $theme.colors.backgroundAlwaysLight,
          };
        }
      },
    },
    ClearIcon: {
      props: {
        overrides: {
          Svg: {
            component: function InputClearIconComponent({
              children,
              size,
              viewBox,
              tabIndex,
              ...iconProps
            }: unknown) {
              return (
                <InputClearIcon
                  {...iconProps}
                  width={iconSize}
                  height={iconSize}
                  tabIndex={`${tabIndex}`}
                  //A little hack to get insert a title node for accessibility
                >
                  {children[0] /*gets the title of the svg if passed via props*/}
                </InputClearIcon>
              );
            },
          },
        },
      },
    },
  } as Overrides<
    BaseInput & { $isFocused: boolean; $disabled: boolean; $positive: boolean; $error: boolean }
  >;
};

const StyledSearchIcon = styled(Search)<{ disabled?: boolean }>`
  fill: ${({ disabled }) => (disabled ? baseuiTheme.colors.mono400 : "currentColor")};
`;

//Decided to use a function instead of a component because there some cases in which the value of endEnhancer and startEnhancer should be falsy to prevent any unnecesarry spacing styles from being applied when an icon is not present.
const getEndEnhancer = (props: InputProps) => {
  const iconSize = getIconSize(props.size);

  switch (props.status) {
    case STATUS.Complete:
      return <CheckmarkIcon size={20} width={iconSize} height={iconSize} />;
    case STATUS.Incomplete:
      return <CloseIcon size={20} width={iconSize} height={iconSize} />;
    default:
      return props.rightIcon;
  }
};

const getStartEnhancer = (props: InputProps) => {
  const iconSize = getIconSize(props.size);
  return props.search ? (
    <StyledSearchIcon size={20} width={iconSize} height={iconSize} disabled={props.disabled} />
  ) : (
    props.leftIcon
  );
};

const index = (componentProps: InputProps) => {
  const { overrides, rightIcon, leftIcon, currentCharacterCount, maxCharacterCount, ...props } =
    componentProps;

  return (
    <>
      {currentCharacterCount !== undefined && maxCharacterCount && (
        <StyledCharacterCounter $danger={currentCharacterCount > maxCharacterCount}>
          {currentCharacterCount}
          {maxCharacterCount ? ` / ${maxCharacterCount}` : ""}
        </StyledCharacterCounter>
      )}
      <BaseInput
        {...props}
        overrides={mergeOverrides(
          getOverrides(componentProps),
          overrides as Overrides<
            BaseInput & {
              $isFocused: boolean;
              $disabled: boolean;
              $error: boolean;
              $positive: boolean;
            }
          >,
        )}
        endEnhancer={getEndEnhancer(componentProps)}
        startEnhancer={getStartEnhancer(componentProps)}
      />
    </>
  );
};

export default index;
